/**
 * Breadcrumb Tracking
 *
 * Records user actions and events leading up to an error to provide debugging context.
 * Breadcrumbs are stored in a circular buffer (max 30) and include navigation, clicks,
 * API calls, state changes, and console output.
 *
 * Breadcrumbs help answer "what was the user doing before the error occurred?"
 *
 * @module lib/datahub/errorLogging/breadcrumbs
 *
 * @example
 * ```typescript
 * import { addBreadcrumb, recordNavigation, setupAutoBreadcrumbs } from './breadcrumbs';
 *
 * // Manual breadcrumb
 * addBreadcrumb('click', 'ui', 'User clicked submit button', { formId: 'checkout' });
 *
 * // Navigation breadcrumb
 * recordNavigation('/quotes', '/quotes/123');
 *
 * // Auto-capture breadcrumbs (call once at app startup)
 * const cleanup = setupAutoBreadcrumbs();
 * ```
 */

import type { Breadcrumb } from './types';

const MAX_BREADCRUMBS = 30;
const breadcrumbs: Breadcrumb[] = [];

/**
 * Add a breadcrumb to the trail
 *
 * Adds a breadcrumb to the in-memory trail. Automatically maintains the
 * circular buffer by removing oldest breadcrumbs when limit is reached.
 *
 * @param type - Type of breadcrumb (navigation, click, api_call, etc.)
 * @param category - Category for grouping (ui, network, state, etc.)
 * @param message - Human-readable description of the action
 * @param data - Optional structured data about the action
 *
 * @example
 * ```typescript
 * addBreadcrumb('api_call', 'network', 'POST /api/quotes', {
 *   url: '/api/quotes',
 *   method: 'POST'
 * });
 * ```
 */
export function addBreadcrumb(
  type: Breadcrumb['type'],
  category: string,
  message: string,
  data?: Record<string, unknown>
): void {
  const breadcrumb: Breadcrumb = {
    timestamp: Date.now(),
    type,
    category,
    message,
    data,
  };

  breadcrumbs.push(breadcrumb);

  // Keep only the last N breadcrumbs
  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs.shift();
  }
}

/**
 * Get a copy of current breadcrumbs
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return [...breadcrumbs];
}

/**
 * Clear all breadcrumbs (e.g., after successful error submission)
 */
export function clearBreadcrumbs(): void {
  breadcrumbs.length = 0;
}

/**
 * Add a navigation breadcrumb
 */
export function recordNavigation(from: string, to: string): void {
  addBreadcrumb('navigation', 'route', `Navigate from ${from} to ${to}`, {
    from,
    to,
  });
}

/**
 * Add a click breadcrumb
 */
export function recordClick(target: string, componentName?: string): void {
  addBreadcrumb('click', 'ui', `Click: ${target}`, {
    target,
    componentName,
  });
}

/**
 * Add an API call breadcrumb
 */
export function recordApiCall(
  method: string,
  url: string,
  _data?: Record<string, unknown>
): void {
  // Don't include sensitive data in breadcrumbs
  const sanitizedUrl = url.replace(/token=[^&]+/gi, 'token=[REDACTED]');
  addBreadcrumb('api_call', 'network', `${method} ${sanitizedUrl}`, {
    method,
    url: sanitizedUrl,
  });
}

/**
 * Add an API response breadcrumb
 */
export function recordApiResponse(
  method: string,
  url: string,
  status: number,
  duration?: number
): void {
  const sanitizedUrl = url.replace(/token=[^&]+/gi, 'token=[REDACTED]');
  addBreadcrumb('api_response', 'network', `${status} ${method} ${sanitizedUrl}`, {
    method,
    url: sanitizedUrl,
    status,
    duration,
  });
}

/**
 * Add a state change breadcrumb
 */
export function recordStateChange(
  action: string,
  details?: Record<string, unknown>
): void {
  addBreadcrumb('state_change', 'state', action, details);
}

/**
 * Add a console breadcrumb (for capturing console.error/warn)
 */
export function recordConsole(
  level: 'error' | 'warn' | 'info',
  message: string
): void {
  addBreadcrumb('console', level, message);
}

/**
 * Setup automatic breadcrumb collection
 *
 * Installs event listeners and console interceptors to automatically capture
 * breadcrumbs for clicks and console errors/warnings. Call this once at app
 * initialization.
 *
 * @returns Cleanup function to remove listeners and restore console methods
 *
 * @example
 * ```typescript
 * // In App.tsx or main.tsx
 * useEffect(() => {
 *   const cleanup = setupAutoBreadcrumbs();
 *   return cleanup; // Cleanup on unmount
 * }, []);
 * ```
 */
export function setupAutoBreadcrumbs(): () => void {
  const cleanupFns: Array<() => void> = [];

  // Track clicks
  const clickHandler = (event: MouseEvent) => {
    const target = event.target as Element;
    if (!target) return;

    // Get meaningful target description
    const tagName = target.tagName.toLowerCase();
    const text = target.textContent?.slice(0, 50) || '';
    const id = target.id ? `#${target.id}` : '';
    // Handle SVG elements where className is SVGAnimatedString, not a string
    // Use getAttribute for cross-browser compatibility with both HTML and SVG elements
    const classNameStr = target.getAttribute('class') || '';
    const className = classNameStr
      ? `.${classNameStr.split(' ').filter(Boolean).slice(0, 2).join('.')}`
      : '';

    const description = `${tagName}${id}${className}${text ? `: "${text}"` : ''}`;
    recordClick(description);
  };

  document.addEventListener('click', clickHandler, { capture: true });
  cleanupFns.push(() => document.removeEventListener('click', clickHandler, { capture: true }));

  // Track console errors/warnings
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  console.error = (...args: unknown[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    recordConsole('error', message.slice(0, 200));
    originalConsoleError.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    const message = args.map(arg =>
      typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
    ).join(' ');
    recordConsole('warn', message.slice(0, 200));
    originalConsoleWarn.apply(console, args);
  };

  cleanupFns.push(() => {
    console.error = originalConsoleError;
    console.warn = originalConsoleWarn;
  });

  // Return cleanup function
  return () => {
    cleanupFns.forEach(fn => fn());
  };
}
